import * as React from 'react'
import { useParams } from 'react-router-dom'

import { F0ActionBar } from '@factorialco/f0-react/dist/experimental'
import { Delete, Replace } from '@factorialco/f0-react/icons/app'
import {
  Background,
  Controls,
  type OnEdgesChange,
  type OnNodesChange,
  ReactFlow,
  type Edge as ReactFlowEdge,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { TreeFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import DeleteJobCatalogTreeNodeModal from 'modules/job_catalog/components/DeleteJobCatalogTreeNodeModal'
import Edge from 'modules/job_catalog/components/DevJobCatalogTree/components/Edge'
import JobCatalogLevelNode from 'modules/job_catalog/components/DevJobCatalogTree/components/JobCatalogLevelNode'
import Node from 'modules/job_catalog/components/DevJobCatalogTree/components/Node'
import { TreeContext } from 'modules/job_catalog/components/DevJobCatalogTree/context'
import { type CustomNode } from 'modules/job_catalog/components/DevJobCatalogTree/types'
import useExpandCollapse from 'modules/job_catalog/components/DevJobCatalogTree/useExpandCollapse'
import useMultiSelect from 'modules/job_catalog/components/DevJobCatalogTree/useMultiSelect'
import useNodeNavigation from 'modules/job_catalog/components/DevJobCatalogTree/useNodeNavigation'
import useNodeSelectionFromRoute from 'modules/job_catalog/components/DevJobCatalogTree/useNodeSelectionFromRoute'
import useToggleExpand from 'modules/job_catalog/components/DevJobCatalogTree/useToggleExpand'
import MoveJobCatalogTreeNodeModal from 'modules/job_catalog/components/MoveJobCatalogTreeNodeModal'
import { useTreeNode } from 'modules/job_catalog/hooks/useTreeNode'
import {
  NODE_TYPES,
  getChildNodeType,
  getTextFromNodeType,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

import { getParentNode } from './treeUtils'
import useTreeDataToNodes from './useTreeDataToNodes'

interface Props {
  width: number
  height: number
  treeData: TreeFragment
}

const JobCatalogTree = ({ width, height, treeData }: Props) => {
  const params = useParams<{ id?: string }>()
  const initialData = useTreeDataToNodes(treeData)
  const [showDeleteNodeModal, setShowDeleteNodeModal] = React.useState(false)
  const [showMoveNodeModal, setShowMoveNodeModal] = React.useState(false)
  const [nodes, setNodes] = React.useState<CustomNode[]>(initialData.nodes)
  const [edges, setEdges] = React.useState<ReactFlowEdge[]>(initialData.edges)

  // Sync nodes and edges when treeData changes (e.g., after refetch)
  const prevTreeDataRef = React.useRef(treeData)
  React.useEffect(() => {
    if (prevTreeDataRef.current !== treeData) {
      setNodes(initialData.nodes)
      setEdges(initialData.edges)
      prevTreeDataRef.current = treeData
    }
  }, [treeData, initialData.nodes, initialData.edges])

  const { onNodeClick } = useNodeNavigation()
  const { onToggleExpand } = useToggleExpand({ setNodes })
  const { checkedNodes, onMultiSelectNode, onUnselectAll } = useMultiSelect({
    nodes,
    edges,
    setNodes,
  })
  const { nodes: visibleNodes, edges: visibleEdges } = useExpandCollapse(nodes, edges)
  // TODO: handle multiple selected nodes
  const checkedNodeType = normalizeNodeType(checkedNodes[0]?.data.type || '')
  const textFromNodeTypeSummary = getTextFromNodeType(
    checkedNodeType,
    checkedNodes.length
  ).toLowerCase()

  const { currentAttributes } = useTreeNode(checkedNodes[0]?.data.node_uuid)
  const parentNode = React.useMemo(
    () =>
      checkedNodes.length > 0
        ? getParentNode(checkedNodes[0]?.data.node_uuid || '', edges, nodes)
        : undefined,
    [checkedNodes, edges, nodes]
  )

  const canMoveNode = React.useMemo(
    () =>
      (checkedNodeType === NODE_TYPES.FUNCTION || checkedNodeType === NODE_TYPES.ROLE) &&
      currentAttributes.every(
        (attr) => attr.type === 'salary_range' || attr.definedAt !== checkedNodes[0]?.data.type
      ),
    [currentAttributes, checkedNodeType, checkedNodes]
  )

  useNodeSelectionFromRoute({
    nodeId: params.id,
    setNodes,
    edges,
  })

  const onNodesChange: OnNodesChange<CustomNode> = React.useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  )
  const onEdgesChange: OnEdgesChange = React.useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  )

  const onMoveNode = (success: boolean) => {
    setShowMoveNodeModal(false)
    if (success) {
      onUnselectAll()
    }
  }

  const onDeleteNode = (success: boolean) => {
    setShowDeleteNodeModal(false)
    if (success) {
      setNodes((nds) => nds.filter((n) => n.data.node_uuid !== checkedNodes[0]?.data.node_uuid))
      setEdges((eds) =>
        eds.filter(
          (e) =>
            e.source !== checkedNodes[0]?.data.node_uuid &&
            e.target !== checkedNodes[0]?.data.node_uuid
        )
      )
      onUnselectAll()
    }
  }

  const edgeTypes = {
    'custom-edge': Edge,
  }

  const nodeTypes = {
    'custom-node': Node,
    'custom-level-node': JobCatalogLevelNode,
  }

  return (
    <TreeContext.Provider value={{ onToggleExpand, onMultiSelectNode }}>
      <ReactFlow
        fitView
        nodes={visibleNodes}
        edges={visibleEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        width={width}
        height={height}
        minZoom={0.2}
        maxZoom={2}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onlyRenderVisibleElements={false}
        onNodeClick={onNodeClick}
        proOptions={{
          hideAttribution: true,
        }}
      >
        <F0ActionBar
          isOpen={checkedNodes.length > 0}
          label={i18n.t('job_catalog.job_catalog_tree.action_bar.label_with_node_type', {
            count_type: textFromNodeTypeSummary,
          })}
          primaryActions={[
            {
              label: i18n.t('job_catalog.job_catalog_tree.action_bar.primary_actions.move_to'),
              onClick: () => setShowMoveNodeModal(true),
              icon: Replace,
              disabled: !canMoveNode,
            },
          ]}
          secondaryActions={[
            {
              label: i18n.t('common.delete'),
              onClick: () => setShowDeleteNodeModal(true),
              icon: Delete,
              critical: true,
            },
            { label: i18n.t('common.unselect'), onClick: onUnselectAll },
          ]}
        />
        <Controls showInteractive={false} position="top-left" />
        <Background bgColor="#F9F9FB" color="#BFBFBF" />
      </ReactFlow>
      {showDeleteNodeModal && (
        <DeleteJobCatalogTreeNodeModal
          nodeId={checkedNodes[0]?.data.node_uuid || ''}
          nodeName={checkedNodes[0]?.data.name || ''}
          nodeType={checkedNodeType}
          childrenNodeType={getChildNodeType(checkedNodes[0]?.data.type || '')}
          onSubmit={onDeleteNode}
          onClose={() => {
            setShowDeleteNodeModal(false)
          }}
          // TODO: this can be set to something like nodeToUse.people.length === 0 when we can have the people count precalculated
          //canBeDeleted={nodeToUse.people_count === 0}
          canBeDeleted
        />
      )}
      {showMoveNodeModal && (
        <MoveJobCatalogTreeNodeModal
          nodeName={checkedNodes[0]?.data.name || ''}
          nodeUuid={checkedNodes[0]?.data.node_uuid || ''}
          parentNodeUuid={parentNode?.data.node_uuid || ''}
          onSubmit={onMoveNode}
          onClose={() => setShowMoveNodeModal(false)}
        />
      )}
    </TreeContext.Provider>
  )
}

const DevJobCatalogTree = ({ width, height, treeData }: Props) => {
  return (
    <ReactFlowProvider>
      <JobCatalogTree width={width} height={height} treeData={treeData} />
    </ReactFlowProvider>
  )
}

export default DevJobCatalogTree
